// auth.ts
import api from "@/api/api";
import { setStorageValue } from "@/utils/storage";
import { clearCache } from "@/utils/cache";
import { ref } from "@vue/runtime-core";
import { defineStore } from "pinia";
import { useRouter } from "vue-router";
import { useBrandStore } from "./brand";

interface GroupPivot {
  user_id: number;
  group_id: number;
}

interface Group {
  id: number;
  name: string;
  brand: string;
  slug: string;
  pivot: GroupPivot;
}

interface UserDetails {
  id: number;
  user_id: number;
  name: string;
  description: string | null;
  address: string;
  postal_code: string;
  city: string;
  telephone: string;
  email: string;
  contact_lastname: string;
  contact_firstname: string;
  contact_telephone: string;
  contact_email: string;
  website: string;
  facebook: string | null;
  linkedin: string | null;
  instagram: string | null;
}

interface UserSignature {
  id: number;
  user_id: number;
  image_name: string;
  updated_at: string;
  deleted_at: string | null;
  created_at: string | null;
}

interface Support {
  id: number;
  name: string;
  dates: string;
  price_type: number;
  support_template_id: number;
  published: boolean;
  agco: boolean;
  support_header_id: number;
  owner_id: number;
  nb_pages: number;
  support_group_id: number;
  thumb_name: string | null;
  highlight_bg: boolean;
  format: string | null;
  pdf_name: string | null;
  highlight_bg_color: string;
  display_offre: string;
  date_publish: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

interface User {
  id: number;
  uuid: string;
  roles: string[];
  status: boolean;
  sector_manager_id: number | null;
  general_configuration_id: number | null;
  accessories: boolean;
  email: string | null;
  email_verified_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  groups: Group[];
  user_details: UserDetails | null;
  sector_manager: any | null;
  orders: any[];
  user_logo: any | null;
  user_signatures: UserSignature[];
  documents: any[];
  products: any[];
  user_routing_files: any[];
  supports: Support[];
  contacts: any[];
  user_catalogs: any[];
  general_configuration: any | null;
  // Champs spécifiques au front
  token?: string;
  brand?: string;
}

export const useAuthStore = defineStore("auth", () => {
  const router = useRouter();
  const brandStore = useBrandStore();

  const user = ref<User | null>(null);
  const isAuthenticated = ref(false);
  const error = ref<string | null>(null);
  const token = ref<string | null>(localStorage.getItem("auth_token"));
  const tempToken = ref<string | null>(null);
  const loading = ref(false);
  const initialized = ref(false);
  const emailMissing = ref(false);

  // Fonction pour réinitialiser le store et purger tout résidu user
  function reset() {
    user.value = null;
    isAuthenticated.value = false;
    error.value = null;
    token.value = null;
    tempToken.value = null;
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user_data");
    localStorage.removeItem("user_logo");
    localStorage.removeItem("user_signatures");
    localStorage.removeItem("brand_assets_cache");
    localStorage.removeItem("cache_timestamp");
    localStorage.removeItem("cached_providers");
    localStorage.removeItem("catalogs_cache");
    localStorage.removeItem("documents_cache");
    localStorage.removeItem("helps");
    localStorage.removeItem("operation_cache");
    localStorage.removeItem("agco_documents_cache");
    localStorage.removeItem("redirect_after_brand_change");
    localStorage.removeItem("last_brand_change_time");
    sessionStorage.clear();
    delete api.defaults.headers.common["Authorization"];

    // Vider IndexedDB en best-effort (asynchrone, fire-and-forget)
    void clearCache();

    // Réinitialiser le brand
    brandStore.reset();
  }

  // Fonction pour sauvegarder la session
  function saveSession(userData: User, authToken: string) {
    user.value = userData;
    token.value = authToken;
    localStorage.setItem("auth_token", authToken);
    localStorage.setItem("user_data", JSON.stringify(userData));
    api.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;
    isAuthenticated.value = true;

    // Vérifier si l'email est renseigné dans le profil
    const userEmail = userData.user_details?.email || userData.user_details?.contact_email || userData.email;
    emailMissing.value = !userEmail || userEmail.trim() === '';

    // Mettre à jour le brand automatiquement depuis les groupes de l'utilisateur
    if (userData.groups && userData.groups.length > 0) {
      const hasValidBrand = brandStore.updateFromUserData(userData.groups);

      if (!hasValidBrand) {
        // Si l'utilisateur n'a pas de groupe valide, rediriger vers ChooseWorkspace
        router.push("/choose-workspace");
      }
    }
  }

  // Fonction pour vérifier et restaurer la session
  async function checkAuth() {
    if (initialized.value) {
      return isAuthenticated.value;
    }

    const storedToken = localStorage.getItem("auth_token");
    if (!storedToken) {
      reset();
      initialized.value = true;
      return false;
    }

    try {
      loading.value = true;
      token.value = storedToken;
      api.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;

      // Vérifier si on a déjà les données en cache
      const cachedUserData = localStorage.getItem("user_data");
      if (cachedUserData) {
        const userData = JSON.parse(cachedUserData);
        user.value = userData;
        isAuthenticated.value = true;
        initialized.value = true;

        // Enregistrer la connexion pour les statistiques même avec des données en cache
        try {
          await api.post("/auth/track-connection");
        } catch (err) {
          console.error("Erreur lors de l'enregistrement de la connexion:", err);
        }

        return true;
      }

      // Si pas de cache, on fait l'appel à /me
      const response = await api.get("/me");
      if (response.data) {
        const userData: User = {
          ...response.data,
          token: storedToken,
        };

        user.value = userData;
        localStorage.setItem("user_data", JSON.stringify(userData));
        isAuthenticated.value = true;
        initialized.value = true;

        // Mettre à jour le brand depuis les groupes
        if (userData.groups && userData.groups.length > 0) {
          brandStore.updateFromUserData(userData.groups);
        }

        // Enregistrer la connexion pour les statistiques
        try {
          await api.post("/auth/track-connection");
        } catch (err) {
          console.error("Erreur lors de l'enregistrement de la connexion:", err);
        }

        return true;
      }

      reset();
      initialized.value = true;
      return false;
    } catch (err) {
      console.error("Erreur lors de la vérification de la session:", err);
      reset();
      initialized.value = true;
      return false;
    } finally {
      loading.value = false;
    }
  }

  // Fonction pour forcer la mise à jour des données utilisateur
  async function refreshUserData() {
    try {
      const response = await api.get("/me");

      if (response.data) {
        // Récupérer les données existantes du localStorage
        const existingUserData = localStorage.getItem("user_data");
        let userData: User;

        if (existingUserData) {
          // Fusionner les données existantes avec les nouvelles données
          const parsedExistingData = JSON.parse(existingUserData);
          userData = {
            ...parsedExistingData,
            ...response.data,
            token: token.value,
          };
        } else {
          // Créer un nouvel objet utilisateur
          userData = {
            ...response.data,
            token: token.value,
          };
        }

        // S'assurer que les user_details sont présents
        if (!userData.user_details && response.data.user_details) {
          userData.user_details = response.data.user_details;
        }

        user.value = userData;
        setStorageValue('user_data', userData as any);

        // Mettre à jour le brand depuis les groupes
        if (userData.groups && userData.groups.length > 0) {
          brandStore.updateFromUserData(userData.groups);
        }

        return true;
      }
      return false;
    } catch (err) {
      console.error(
        "Erreur lors de la mise à jour des données utilisateur:",
        err
      );
      return false;
    }
  }

  // Initialiser la vérification de session au montage du store
  if (token.value && !initialized.value) {
    checkAuth();
  } else {
    initialized.value = true;
  }

  // Étape 1 : Obtenir le token temporaire
  async function getAuthToken(uuid: string, password: string) {
    try {
      loading.value = true;
      error.value = null;

      // On envoie le mot de passe en clair, Laravel s'occupera du hashage bcrypt
      const response = await api.post("/auth/token", {
        uuid,
        password,
      });

      tempToken.value = response.data.token;
      return response.data;
    } catch (err: any) {
      console.error("Erreur lors de l'obtention du token:", err);
      error.value =
        err.response?.data?.message || "Erreur lors de l'obtention du token";
      throw err;
    } finally {
      loading.value = false;
    }
  }

  // Étape 2 : Se connecter avec le token temporaire
  async function login(username: string, password: string) {
    try {
      loading.value = true;
      error.value = null;

      // Première étape : obtenir le token temporaire
      await getAuthToken(username, password);

      if (!tempToken.value) {
        throw new Error("Token temporaire non reçu");
      }

      // Deuxième étape : se connecter avec le token
      const response = await api.post("/login", {
        uuid: username,
        auth_token: tempToken.value,
      });

      if (response.data && response.data.user) {
        // Récupérer les données existantes du localStorage
        const existingUserData = localStorage.getItem("user_data");
        let userData: User;

        if (existingUserData) {
          // Fusionner les données existantes avec les nouvelles données
          const parsedExistingData = JSON.parse(existingUserData);
          userData = {
            ...parsedExistingData,
            ...response.data.user,
            token: response.data.access_token,
          };
        } else {
          // Créer un nouvel objet utilisateur
          userData = {
            ...response.data.user,
            token: response.data.access_token,
          };
        }

        // S'assurer que les user_details sont présents
        if (!userData.user_details && response.data.user.user_details) {
          userData.user_details = response.data.user.user_details;
        }

        saveSession(userData, response.data.access_token);
        isAuthenticated.value = true;

        // Redirection gérée dans saveSession via brandStore
        if (!brandStore.currentBrand) {
          await router.push("/choose-workspace");
        } else {
          await router.push("/");
        }
        return true;
      }

      error.value = "Identifiants incorrects";
      const event = new CustomEvent("show-notification", {
        detail: {
          type: "error",
          title: "Erreur",
          message: "Identifiants incorrects. Veuillez réessayer.",
        },
      });
      window.dispatchEvent(event);
      return false;
    } catch (err: any) {
      console.error("Erreur de connexion:", err);
      if (err.response) {
        console.log("Error Response:", err.response.data);
        error.value =
          err.response.data.message || "Erreur lors de la connexion";
        const event = new CustomEvent("show-notification", {
          detail: {
            type: "error",
            title: "Erreur",
            message: err.response.data.message || "Erreur lors de la connexion",
          },
        });
        window.dispatchEvent(event);
      } else if (err instanceof Error) {
        error.value = err.message;
        const event = new CustomEvent("show-notification", {
          detail: {
            type: "error",
            title: "Erreur",
            message: err.message,
          },
        });
        window.dispatchEvent(event);
      }
      return false;
    } finally {
      loading.value = false;
    }
  }

  // Fonction pour la connexion directe avec token temporaire
  async function loginWithTempToken(uuid: string, tempToken: string) {
    try {
      loading.value = true;
      error.value = null;

      // Se connecter directement avec le token temporaire
      const response = await api.post("/login", {
        uuid: uuid,
        auth_token: tempToken,
      });

      if (response.data && response.data.user) {
        // Sauvegarder d'abord le token pour les requêtes suivantes
        const authToken = response.data.access_token;
        token.value = authToken;
        localStorage.setItem("auth_token", authToken);
        api.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;

        // Récupérer TOUTES les données utilisateur via /me pour avoir generalConfiguration et autres
        const meResponse = await api.get("/me");
        
        if (meResponse.data) {
          const userData: User = {
            ...meResponse.data,
            token: authToken,
          };

          saveSession(userData, authToken);
          isAuthenticated.value = true;

          // Enregistrer la connexion pour les statistiques
          try {
            await api.post("/auth/track-connection");
          } catch (err) {
            console.error("Erreur lors de l'enregistrement de la connexion:", err);
          }

          // Redirection gérée dans saveSession via brandStore
          if (!brandStore.currentBrand) {
            await router.push("/choose-workspace");
          } else {
            await router.push("/");
          }
          return true;
        }
      }

      error.value = "Token temporaire invalide ou expiré";
      const event = new CustomEvent("show-notification", {
        detail: {
          type: "error",
          title: "Erreur",
          message: "Token temporaire invalide ou expiré.",
        },
      });
      window.dispatchEvent(event);
      return false;
    } catch (err: any) {
      console.error("Erreur de connexion avec token temporaire:", err);
      if (err.response) {
        error.value =
          err.response.data.message || "Erreur lors de la connexion";
        const event = new CustomEvent("show-notification", {
          detail: {
            type: "error",
            title: "Erreur",
            message: err.response.data.message || "Erreur lors de la connexion",
          },
        });
        window.dispatchEvent(event);
      } else if (err instanceof Error) {
        error.value = err.message;
        const event = new CustomEvent("show-notification", {
          detail: {
            type: "error",
            title: "Erreur",
            message: err.message,
          },
        });
        window.dispatchEvent(event);
      }
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    try {
      const currentToken = localStorage.getItem("auth_token");
      if (currentToken) {
        // Enregistrer la déconnexion côté backend
        try {
          await api.post("/auth/track-disconnection");
        } catch (trackErr) {
          console.error("Erreur lors du tracking de déconnexion:", trackErr);
        }

        // S'assurer que le token est dans les headers pour la requête de déconnexion
        await api.post(
          "/logout",
          {},
          {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          }
        );
      }

      // Nettoyer la session
      reset();

      // Rediriger vers la page de connexion
      router.push("/login");
    } catch (err) {
      console.error("Erreur lors de la déconnexion:", err);
      // Même en cas d'erreur, on nettoie la session
      reset();
      router.push("/login");
    }
  }

  const setBrand = async (brand: string) => {
    if (!user.value) {
      throw new Error("Utilisateur non connecté");
    }

    try {
      // Mettre à jour le brand via le store centralisé
      brandStore.setBrand(brand);

      await router.push("/");
    } catch (err: any) {
      error.value = err.message;
      throw err;
    }
  };

  const fetchUserData = async () => {
    try {
      const response = await api.get("/user");
      if (response.data) {
        const userData: User = {
          ...response.data,
          token: token.value,
          brand: response.data.brand,
        };

        user.value = userData;
        isAuthenticated.value = true;
        return true;
      }
      return false;
    } catch (err) {
      console.error(
        "Erreur lors de la récupération des données utilisateur:",
        err
      );
      error.value = "Erreur lors de la récupération des données utilisateur";
      return false;
    }
  };

  // Méthode de debug pour forcer le tracking de connexion
  async function forceTrackConnection() {
    try {
      console.log("🔍 Debug: Forcing track connection...");
      const response = await api.post("/auth/track-connection");
      console.log("✅ Debug: Track connection response:", response.data);
      
      // Vérifier aussi les données utilisateur actuelles
      const meResponse = await api.get("/me");
      console.log("👤 Debug: Current user data:", meResponse.data);
      console.log("⏰ Debug: Current time:", new Date().toISOString());
      console.log("⏰ Debug: User last_login_at:", meResponse.data.last_login_at);
      
      return response.data;
    } catch (err) {
      console.error("❌ Debug: Error in track connection:", err);
      throw err;
    }
  }

  return {
    user,
    isAuthenticated,
    error,
    loading,
    initialized,
    login,
    loginWithTempToken,
    logout,
    setBrand,
    reset,
    fetchUserData,
    checkAuth,
    refreshUserData,
    forceTrackConnection,
    emailMissing,
  };
});
